import React from "react"

interface MediaState {
  initialized: boolean
  observer: MutationObserver | null
  trackedElements: Set<HTMLMediaElement>
}

export const useOneMediaAtATime = () => {
  const media = React.useRef<MediaState>({
    initialized: false,
    observer: null,
    trackedElements: new Set()
  }).current

  React.useEffect(() => {
    if (media.initialized) return

    media.initialized = true
    const controller = new AbortController()
    const mediaSelector = "audio, video"

    const handlePlay = (event: Event) => {
      const element = event.target as HTMLMediaElement
      media.trackedElements.forEach((mediaElement: HTMLMediaElement) => {
        if (mediaElement !== element && !mediaElement.paused) {
          try {
            mediaElement.pause()
          } catch (error) {
            console.error(`Failed to pause media element: ${error}`)
          }
        }
      })
    }

    const addElement = (element: HTMLMediaElement) => {
      if (media.trackedElements.has(element)) return
      try {
        media.trackedElements.add(element)
        element.addEventListener("play", handlePlay, { signal: controller.signal })
      } catch (error) {
        console.error(`Failed to add media element: ${error}`)
      }
    }

    const removeElement = (element: HTMLMediaElement) => {
      media.trackedElements.delete(element)
    }

    const addListeners = (nodes: NodeListOf<Element> | Element[]) => {
      nodes.forEach((node) => {
        if (node instanceof HTMLMediaElement) {
          addElement(node)
        }
      })
    }

    addListeners(document.querySelectorAll(mediaSelector))

    media.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return
          const medias = node.matches(mediaSelector)
            ? [node]
            : Array.from(node.querySelectorAll?.(mediaSelector) ?? [])
          medias.forEach((media) => {
            if (media instanceof HTMLMediaElement) {
              addElement(media)
            }
          })
        })

        mutation.removedNodes.forEach((node) => {
          if (!(node instanceof Element)) return
          const medias =
            node instanceof HTMLMediaElement
              ? [node]
              : Array.from(node.querySelectorAll?.(mediaSelector) ?? [])
          medias.forEach((media) => {
            if (media instanceof HTMLMediaElement) {
              removeElement(media)
            }
          })
        })
      })
    })

    media.observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    return () => {
      media.trackedElements.forEach(removeElement)
      controller.abort()
      media.observer?.disconnect()
      media.initialized = false
    }
  }, [])
}
